<template>
  <div v-if="sellerFlashDeal">
    <PriceDueToday v-if="shouldDisplayPriceDueToday" :price-due-today />

    <HighlightedPrice
      :price
      :price-attrs="{
        'data-qa': 'productpage-product-price',
        'data-test': 'productpage-product-price',
      }"
    >
      <template #label>
        <PriceMainLabel
          v-if="shouldDisplayPriceLabel"
          :buyback-offer
          class="text-static-success-hi"
          :is-from-google-shopping
          :mobile-plan-offer="mobilePlan"
        />
      </template>
    </HighlightedPrice>

    <SellerFlashSalesSavings
      v-if="shouldDisplaySavings"
      :last-lowest-price="sellerFlashDeal.percentage.minimumPrice"
      :price
    />

    <LastLowestPrice
      class="relative z-[1]"
      :last-lowest-price="sellerFlashDeal.percentage.minimumPrice"
    />

    <div class="flex flex-col flex-wrap">
      <FormattedMessage
        v-if="withAssuranceFee"
        class="caption mt-4"
        :definition="translations.qualityAssuranceFee"
      >
        <template #qualityAssuranceFeeLink>
          <ServiceFeeModal />
        </template>
      </FormattedMessage>
      <VatIncluded class="mt-4" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product.js'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers.js'
import type { GetOfferV1Response } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/api/types'
import HighlightedPrice from '@backmarket/nuxt-layer-deals/HighlightedPrice.vue'
import LastLowestPrice from '@backmarket/nuxt-layer-deals/LastLowestPrice.vue'
import PriceDueToday from '@backmarket/nuxt-layer-deals/PriceDueToday.vue'
import SellerFlashSalesSavings from '@backmarket/nuxt-layer-deals/SellerFlashSalesSavings.vue'
import ServiceFeeModal from '@backmarket/nuxt-layer-product-modals/ServiceFeeModal.vue'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep.js'

import translations from './Price.translations'
import PriceMainLabel from './components/PriceLabel/components/PriceMainLabel.vue'
import VatIncluded from './components/VatIncluded/VatIncluded.vue'

const props = defineProps<{
  price: Price
  product: GetProductResponse
  selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
  buybackOffer?: GetOfferV1Response | null
  mobilePlan?: ReadonlyObjectDeep<MobilePlanOffer>
  withAssuranceFee?: boolean
  isFromGoogleShopping?: boolean
  variant?: 'default' | 'compact'
  withoutPriceDueToday?: boolean
}>()

const sellerFlashDeal = computed(() => {
  const deal = props.selectedOffer.deals?.[0] ?? null

  if (deal?.type !== 'seller_flash_sales') return null

  return deal
})

const priceDueToday = computed(() => props.selectedOffer.price)

const hasBuybackOfferOrMobilePlan = computed(
  () => props.buybackOffer || props.mobilePlan,
)

const shouldDisplayPriceDueToday = computed(
  () => !props.withoutPriceDueToday && props.buybackOffer,
)

const shouldDisplaySavings = computed(
  () => !hasBuybackOfferOrMobilePlan.value && props.variant !== 'compact',
)

const shouldDisplayPriceLabel = computed(
  () => hasBuybackOfferOrMobilePlan.value || props.variant === 'compact',
)
</script>
